import React from 'react';
import { Box, Button, Container, IconButton, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useCarePlans from '../features/plans/useCarePlans';
import { createCarePlan, removeCarePlan, resolveCarePlanReview, updateCarePlan } from '../features/plans/carePlanService';
import colors from '../assets/theme/colors';

const STATUS_LABELS = { suggested: 'Suggested', waiting: 'Waiting', in_progress: 'In progress', completed: 'Completed' };

const PlansPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const childId = params.childId || location.state?.childId || '';
  const { plans, reviews, loading } = useCarePlans(childId);
  const [adding, setAdding] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [editingPlanId, setEditingPlanId] = React.useState('');
  const [removingPlanId, setRemovingPlanId] = React.useState('');

  const addPlan = async () => {
    if (!title.trim()) return;
    await createCarePlan(childId, { title });
    setTitle('');
    setAdding(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: colors.semantic.pageBg, py: 3 }}>
      <Container maxWidth="md">
        <Button onClick={() => navigate('/home', { state: { childId } })} sx={{ textTransform: 'none' }}>← Home</Button>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ my: 2 }}>
          <Box><Typography component="h1" sx={{ fontSize: '1.7rem', fontWeight: 600, color: colors.semantic.ink }}>Plans &amp; next steps</Typography><Typography sx={{ color: colors.semantic.ink2 }}>Active plans for the selected Care Profile.</Typography></Box>
          <Button variant="contained" onClick={() => setAdding(true)} sx={{ textTransform: 'none', boxShadow: 'none' }}>Add plan</Button>
        </Stack>
        {adding ? <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: '18px' }}><Stack spacing={1.2}><TextField label="What needs to happen?" value={title} onChange={(event) => setTitle(event.target.value)} autoFocus /><Stack direction="row" spacing={1}><Button variant="contained" onClick={addPlan}>Add</Button><Button color="inherit" onClick={() => setAdding(false)}>Cancel</Button></Stack></Stack></Paper> : null}
        {loading ? <Typography>Loading…</Typography> : null}
        {reviews.length ? <Stack spacing={1} sx={{ mb: 2 }}><Typography component="h2" sx={{ fontSize: '1rem', fontWeight: 600 }}>Suggested updates to review</Typography>{reviews.map((review) => <Paper key={review.id} variant="outlined" sx={{ p: 2, borderRadius: '18px', bgcolor: colors.semantic.sageMist }}><Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}><Box sx={{ flex: 1 }}><Typography sx={{ fontSize: '0.72rem', color: colors.semantic.ink2, textTransform: 'uppercase' }}>Current plan</Typography><Typography sx={{ fontWeight: 500 }}>{review.current?.title || 'Existing plan'}</Typography><Typography sx={{ mt: 0.3, fontSize: '0.8rem', color: colors.semantic.ink2 }}>{[STATUS_LABELS[review.current?.status], review.current?.targetDateText, review.current?.tentative ? 'Tentative' : ''].filter(Boolean).join(' · ')}</Typography></Box><Box sx={{ flex: 1 }}><Typography sx={{ fontSize: '0.72rem', color: colors.semantic.sageDeep, textTransform: 'uppercase' }}>Suggested from email</Typography><Typography sx={{ fontWeight: 600 }}>{review.proposed?.title || 'Plan update'}</Typography><Typography sx={{ mt: 0.3, fontSize: '0.8rem', color: colors.semantic.ink2 }}>{[STATUS_LABELS[review.proposed?.status], review.proposed?.targetDateText, review.proposed?.tentative ? 'Tentative' : ''].filter(Boolean).join(' · ')}</Typography></Box></Stack><Stack direction="row" spacing={1} sx={{ mt: 1.3 }}><Button variant="contained" onClick={() => resolveCarePlanReview(review, 'accept')}>Accept update</Button><Button color="inherit" onClick={() => resolveCarePlanReview(review, 'dismiss')}>Keep current plan</Button></Stack></Paper>)}</Stack> : null}
        <Stack spacing={1.2}>
          {plans.map((plan) => {
            const isEditing = editingPlanId === plan.id;
            const isRemoving = removingPlanId === plan.id;
            const planDetails = [
              STATUS_LABELS[plan.status || 'suggested'],
              plan.targetDateText || 'No target date',
              plan.tentative ? 'Tentative' : '',
            ].filter(Boolean).join(' · ');

            return <Paper key={plan.id} variant="outlined" sx={{ p: 2, borderRadius: '18px' }}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: colors.semantic.ink }}>{plan.title}</Typography>
                  <Typography sx={{ mt: 0.35, fontSize: '0.82rem', color: colors.semantic.ink2 }}>{planDetails}</Typography>
                </Box>
                <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                  <Tooltip title={isEditing ? 'Done editing' : 'Edit plan'}>
                    <IconButton
                      aria-label={isEditing ? 'Done editing' : 'Edit plan'}
                      onClick={() => {
                        setRemovingPlanId('');
                        setEditingPlanId(isEditing ? '' : plan.id);
                      }}
                      size="small"
                      sx={{ color: colors.semantic.ink2 }}
                    ><EditOutlinedIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Remove plan">
                    <IconButton
                      aria-label="Remove plan"
                      onClick={() => {
                        setEditingPlanId('');
                        setRemovingPlanId(plan.id);
                      }}
                      size="small"
                      sx={{ color: colors.semantic.ink2 }}
                    ><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Stack>
              </Stack>

              {isEditing ? <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 1.5 }}>
                <TextField select size="small" label="Status" value={plan.status || 'suggested'} onChange={(event) => updateCarePlan(plan.id, { status: event.target.value })} sx={{ minWidth: 150 }}>{Object.entries(STATUS_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField>
                <TextField size="small" label="Target date or range" defaultValue={plan.targetDateText || ''} onBlur={(event) => updateCarePlan(plan.id, { targetDateText: event.target.value })} sx={{ flex: 1 }} />
              </Stack> : null}

              {isRemoving ? <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ mt: 1.5 }}>
                <Typography sx={{ mr: 'auto', fontSize: '0.84rem', color: colors.semantic.ink }}>Remove this plan?</Typography>
                <Button color="inherit" onClick={() => setRemovingPlanId('')} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button
                  color="error"
                  onClick={async () => {
                    await removeCarePlan(plan.id);
                    setRemovingPlanId('');
                  }}
                  sx={{ textTransform: 'none' }}
                >Remove</Button>
              </Stack> : null}
            </Paper>;
          })}
          {!loading && !plans.length ? <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: '18px' }}><Typography sx={{ color: colors.semantic.ink2 }}>No active plans yet.</Typography></Paper> : null}
        </Stack>
      </Container>
    </Box>
  );
};

export default PlansPage;
